// ─── Enterprise Observability Logger ─────────────────────────────────────────
// Structured event logging to thesis_audit_log. All events are fire-and-forget
// — logging failures NEVER propagate to the caller.

import { supabase } from '../lib/supabase'

export const EVENT = {
  CREATE_THESIS:       'CREATE_THESIS',
  GENERATE_CHAPTER:    'GENERATE_CHAPTER',
  SAVE_CHAPTER:        'SAVE_CHAPTER',
  VALIDATE_EXPORT:     'VALIDATE_EXPORT',
  BUILD_FIGURES:       'BUILD_FIGURES',
  CREATE_SNAPSHOT:     'CREATE_SNAPSHOT',
  EXPORT_DOCX:         'EXPORT_DOCX',
  EXPORT_HTML:         'EXPORT_HTML',
  REGENERATE_SECTION:  'REGENERATE_SECTION',
  FINALISE_TOC:        'FINALISE_TOC',
  CHAPTER_VERSION:     'CHAPTER_VERSION',
  SNAPSHOT_LOAD:       'SNAPSHOT_LOAD',
  ROLLBACK_CHAPTER:    'ROLLBACK_CHAPTER',
  ACADEMIC_VALIDATE:   'ACADEMIC_VALIDATE',
  FIGURE_VALIDATE:     'FIGURE_VALIDATE',
}

let _userId = null

export function setLoggerUser(userId) {
  _userId = userId
}

/**
 * Log a structured event. Always resolves — never throws.
 *
 * @param {string} eventType  - EVENT constant
 * @param {{ thesisId?, status?, durationMs?, metadata?, error? }} opts
 */
export async function logEvent(eventType, opts = {}) {
  const userId = _userId || (await supabase.auth.getUser()).data?.user?.id
  if (!userId) return

  const row = {
    thesis_id:   opts.thesisId  || null,
    user_id:     userId,
    event_type:  eventType,
    status:      opts.status    || 'OK',
    duration_ms: opts.durationMs ?? null,
    metadata:    opts.metadata  || {},
    error_msg:   opts.error     ? String(opts.error) : null,
  }

  try {
    await supabase.from('thesis_audit_log').insert(row)
  } catch {
    // logging must never crash the caller
  }
}

/**
 * Convenience wrapper that measures duration and logs automatically.
 * Returns the result of fn() or re-throws after logging the error.
 *
 * @param {string} eventType
 * @param {object} opts        - { thesisId, metadata }
 * @param {Function} fn        - async function to time
 */
export async function loggedOp(eventType, opts, fn) {
  const start = Date.now()
  try {
    const result = await fn()
    await logEvent(eventType, { ...opts, status: 'OK', durationMs: Date.now() - start })
    return result
  } catch (err) {
    await logEvent(eventType, { ...opts, status: 'ERROR', durationMs: Date.now() - start, error: err?.message })
    throw err
  }
}
